import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  decommissionSite,
  type AdminSite,
  type LifecycleResponse,
} from '../../api/adminClient';
import { recordPendingSteps } from '../../lib/pending-steps';

/**
 * Two-stage decommission: dry run, then a typed confirmation.
 *
 * NOT `@shared/design-system/ConfirmDialog`, deliberately. That
 * component binds Enter to onConfirm at the window level — correct for
 * a yes/no dialog, actively dangerous here, where the confirmation is
 * a TEXT FIELD you type a site id into and pressing Enter at the end
 * of typing is the most natural thing in the world. It would turn the
 * typed-confirmation safeguard into a formality.
 *
 * The dry run is not optional and not a checkbox: it runs
 * automatically on open, and the destructive button stays disabled
 * until it has returned. `dry_run=true` is what turns "decommission
 * warehouse3" from an abstraction into "12 tombstones, 47 keys" — the
 * only form in which the operator can actually recognise a wrong site.
 */

interface Props {
  site: AdminSite;
  onClose: () => void;
}

export function DecommissionDialog({ site, onClose }: Props) {
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState('');
  const [preview, setPreview] = useState<LifecycleResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const startedPreview = useRef(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (startedPreview.current) return;
    startedPreview.current = true;
    decommissionSite(site.site_id, true).then(
      (resp) => setPreview(resp),
      (err) => setPreviewError(err instanceof Error ? err.message : String(err)),
    );
  }, [site.site_id]);

  const execute = useMutation({
    mutationFn: () => decommissionSite(site.site_id, false),
    onSuccess: (resp) => {
      // Persist BEFORE closing. These steps are the only record that
      // the retired certificate still has bus write access; losing
      // them to an unmount is the failure mode `lib/pending-steps.ts`
      // exists to prevent.
      recordPendingSteps(resp);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sites'] });
      onClose();
    },
  });

  const confirmed = typed.trim() === site.site_id;
  const canExecute = confirmed && preview !== null && !execute.isPending;

  return createPortal(
    <div
      data-nx-modal-layer=""
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="decommission-title"
        data-testid="decommission-dialog"
        style={{
          background: 'var(--nx-card)',
          border: '1px solid var(--nx-error)',
          borderRadius: 'var(--nx-r)',
          padding: 24,
          maxWidth: 640,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2 id="decommission-title" style={{ marginTop: 0, color: 'var(--nx-error)' }}>
          Decommission {site.label}
        </h2>
        <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
          Emits <code>site_retired</code> tombstones for every entity, deletes the site’s cached
          keys from the platform bus, then archives the record. The cached data is not recoverable.
        </p>

        {site.lifecycle_state === 'live' && (
          <p
            style={{
              color: 'var(--nx-warning)',
              fontSize: 13,
              border: '1px solid var(--nx-warning)',
              borderRadius: 'var(--nx-r)',
              padding: '8px 10px',
            }}
          >
            This site is <strong>live</strong> — its worker is publishing right now. Decommissioning
            does not stop the worker; it will keep republishing into a purged keyspace until its
            container is removed.
          </p>
        )}

        <section style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--nx-cyan)' }}>
            Dry run
          </h3>
          {previewError !== null ? (
            <p role="alert" style={{ color: 'var(--nx-error)' }}>
              Dry run failed: {previewError}. Decommissioning is blocked — without a preview there
              is no way to confirm this is the right site.
            </p>
          ) : preview === null ? (
            <p style={{ color: 'var(--nx-muted)' }}>Running dry run…</p>
          ) : (
            <ul data-testid="dry-run-result" style={{ paddingLeft: 20, margin: '6px 0' }}>
              {preview.applied.map((line, i) => (
                <li key={i} style={{ fontSize: 13 }}>
                  {line}
                </li>
              ))}
            </ul>
          )}
        </section>

        {preview !== null && preview.pending_operator_steps.length > 0 && (
          <section style={{ marginTop: 12 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--nx-cyan)' }}>
              You will still have to run
            </h3>
            {/* Shown BEFORE confirming, not only after. Bus access is
                not revoked by this button — an operator who cannot run
                these host commands should find that out now rather
                than after the data is gone. */}
            <ol style={{ paddingLeft: 20, margin: '6px 0' }}>
              {preview.pending_operator_steps.map((step, i) => (
                <li key={i}>
                  <code style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{step}</code>
                </li>
              ))}
            </ol>
          </section>
        )}

        <label style={{ display: 'block', marginTop: 16, fontSize: 13 }}>
          Type <code>{site.site_id}</code> to confirm:
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            // Enter must not submit. See this file's docstring.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            autoComplete="off"
            aria-label={`Type ${site.site_id} to confirm`}
            style={{ marginTop: 6 }}
          />
        </label>

        {execute.isError && (
          <p role="alert" style={{ color: 'var(--nx-error)' }}>
            {execute.error instanceof Error ? execute.error.message : 'Decommission failed'}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!canExecute}
            onClick={() => execute.mutate()}
            data-testid="decommission-execute"
            style={{
              borderColor: 'var(--nx-error)',
              color: canExecute ? 'var(--nx-error)' : undefined,
            }}
          >
            {execute.isPending ? 'Decommissioning…' : 'Decommission permanently'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
